import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { useStores } from '@/hooks/use-stores';
import { MenuType } from '@/store/PIDEditing/PIDEditStore';
import { setDeviceDetailsProps } from './DynamicFormCard.utils';
import { DynamicFormCard } from './DynamicFormCard';
import { useTranslation } from 'react-i18next';
import { useMeasurement } from '@/hooks/useMeasurement';
import { Form } from 'antd';
import { StyledDeviceCard } from './DeviceDetailsCard.styles';
import {
  createDeviceDetailsUtils,
  onCancelHandler,
  ControllerOption,
  ModelOption,
  calculateCardPosition,
  isMenuTypeCreateDevice,
  isMenuTypeDeviceDetails,
} from './DeviceDetailsCard.utils';
import { DeviceType } from '@/constants/deviceTypeEnum';
import {
  ASSET_DEVICE_TYPES,
  isDeviceKindDisconnected,
} from '@/comp/MapBuilder/nodes/nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';

interface FormValues {
  name: string;
  model: string;
  controller?: string;
  pipeDiameter?: number;
  pipeMaterial?: string;
  [key: string]: unknown;
}

const DeviceDetailsCard: React.FC = observer(() => {
  const { pidEditStore } = useStores();
  const { activeMenu } = pidEditStore;
  const cardRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm();
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });
  const { t } = useTranslation();
  const { tvalue, measurementSystem } = useMeasurement();
  const [modelSpecOptions, setModelSpecOptions] = useState<ModelOption[]>([]);
  const [controllerPlaceholder, setControllerPlaceholder] = useState('No Controller');
  const [controllerOptions, setControllerOptions] = useState<ControllerOption[]>([]);
  const [isControllerDisabled, setIsControllerDisabled] = useState(false);

  const isAssetDeviceType = ASSET_DEVICE_TYPES.includes(activeMenu?.context?.deviceType);

  const utils = useMemo(
    () => createDeviceDetailsUtils(form, setModelSpecOptions, setControllerPlaceholder, setControllerOptions),
    [form],
  );

  useEffect(() => {
    const initialize = async () => {
      const isCreateDevice = isMenuTypeCreateDevice(activeMenu?.type);
      const isDeviceDetails = isMenuTypeDeviceDetails(activeMenu?.type, isAssetDeviceType);
      if (isCreateDevice || isDeviceDetails) {
        utils.resetFormState();
        pidEditStore.setDeviceState({ name: '', deviceModelId: null, controllerId: null });

        const { deviceType, entity } = activeMenu.context;

        if (deviceType) {
          const modelSpecPromise = pidEditStore.getModelSpec(deviceType).then(options => {
            const safeOptions = options || [];
            setModelSpecOptions(safeOptions);
            return safeOptions;
          });

          if (isDeviceDetails && entity?.deviceId) {
            await utils.initializeDeviceDetails(entity, modelSpecPromise, pidEditStore, t, tvalue, measurementSystem);
          } else if (isCreateDevice) {
            await utils.initializeCreateDevice(deviceType, modelSpecPromise, pidEditStore, t);
          }
        }
      }
    };

    initialize();
  }, [activeMenu, pidEditStore, form, t, utils]);

  useEffect(() => {
    const { controllerId } = pidEditStore.deviceState;
    const shouldUpdateController =
      activeMenu?.type === MenuType.CreateDevice ||
      (activeMenu?.type === MenuType.DeviceDetails && form.isFieldTouched('model'));

    if (shouldUpdateController) {
      utils.updateControllerDetails(controllerId, pidEditStore, t);
    }
  }, [pidEditStore.deviceState.controllerId, activeMenu, form, pidEditStore, t, utils]);

  useEffect(() => {
    const { deviceKind } = pidEditStore.deviceState;
    setIsControllerDisabled(isDeviceKindDisconnected(deviceKind));
  }, [pidEditStore.deviceState.deviceKind]);

  useEffect(() => {
    // If this dialog is opened from StationReadinessScore, use the position as-is
    if (activeMenu?.context?.fromStationReadiness) {
      setCardPosition(activeMenu.position);
    } else {
      const newPos = calculateCardPosition(activeMenu, cardRef);
      setCardPosition(newPos);
    }
  }, [activeMenu]);

  if (
    !activeMenu ||
    isAssetDeviceType ||
    (activeMenu.type !== MenuType.DeviceDetails && activeMenu.type !== MenuType.CreateDevice)
  ) {
    return null;
  }

  const onCancel = () => {
    onCancelHandler(form, pidEditStore, setControllerPlaceholder, setControllerOptions, t);
  };

  const handleFormChange = (changedValues: Partial<FormValues>) => {
    utils.handleFormChange(changedValues, modelSpecOptions, pidEditStore);
  };

  const onSubmit =
    activeMenu.type === MenuType.CreateDevice
      ? (values: FormValues) => utils.createDevice(values, activeMenu, pidEditStore, t)
      : (values: FormValues) =>
          utils.handleUpdateDevice(values, activeMenu, pidEditStore, t, tvalue, measurementSystem);

  const isModelSpecDisabled = activeMenu.context.deviceType === DeviceType.Connector;

  const deviceDetailsProps = setDeviceDetailsProps(
    activeMenu.type,
    activeMenu.context,
    onCancel,
    onSubmit,
    modelSpecOptions,
    pidEditStore.isLoadingModelSpec,
    'Select Model',
    controllerPlaceholder,
    controllerOptions,
    isModelSpecDisabled,
    isControllerDisabled,
  );

  return (
    <StyledDeviceCard ref={cardRef} position={cardPosition} isFixed={activeMenu?.context?.fromStationReadiness}>
      <DynamicFormCard {...deviceDetailsProps} form={form} onValuesChange={handleFormChange} />
    </StyledDeviceCard>
  );
});

export default DeviceDetailsCard;
